import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography,
} from '@mui/material';
import PlanFeatureList from './PlanFeatureList';
import { buildBasicPlanFeatures } from '../utils/planFeatures';
import { formatMoney } from '../utils/money';

/**
 * What the Basic plan includes, opened from the Active Plan card.
 *
 * REQUESTED as the counterpart to the trial's "See what features are
 * included": an account that has landed on Basic is told what it is on, and
 * nothing on the card said what that comes with. The trial had the answer
 * behind a link and Basic did not, which left the plan an account spends the
 * most time on as the one described the least.
 *
 * ── THE SAME LIST THE BASIC CARD SHOWS ──────────────────────────────────────
 * buildBasicPlanFeatures, rendered by PlanFeatureList — the same builder and
 * the same renderer the Basic column on Manage Plan uses. "Similar to the free
 * trial modal" is satisfied by construction rather than by resemblance: all
 * three surfaces move together when a claim changes, and none of them can
 * quietly keep an old one.
 *
 * `packages` carries the live pack prices, because the first row of that list
 * quotes them. Without it the row is dropped rather than shown empty — see the
 * builder — so this dialog degrades to the features alone rather than to a
 * "Credit pack options" heading with nothing under it.
 *
 * Close is the only action, exactly as on the trial dialog: this answers "what
 * am I on?", and the card underneath already carries both routes onward. An
 * upgrade button here would turn reading into a sales prompt.
 */
export default function BasicFeaturesDialog({ open, onClose, packages = [] }) {
  const features = buildBasicPlanFeatures({
    packages,
    // The formatter the pack picker and the checkout use, so a price cannot
    // read "$5" here and "$5.00" one click later.
    formatPrice: (cents) => formatMoney(cents, 'usd'),
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      aria-labelledby="basic-features-title"
      PaperProps={{ sx: { borderRadius: '20px', p: 1 } }}
    >
      <DialogTitle
        id="basic-features-title"
        sx={{ fontFamily: 'Manrope, sans-serif', fontWeight: 700, pb: 0.5 }}
      >
        Basic plan
        <Typography
          component="p"
          sx={{
            fontFamily: 'Inter, sans-serif', fontWeight: 400, fontSize: 14, color: '#5A5F55', mt: 0.5,
          }}
        >
          See what is available.
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <PlanFeatureList features={features} />
      </DialogContent>

      <DialogActions sx={{ p: 2, pt: 1 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            borderRadius: '10px',
            textTransform: 'none',
            fontWeight: 700,
            bgcolor: '#FFFFFF',
            borderColor: '#DDE0D8',
            color: '#5A5F55',
            '&:hover': { borderColor: '#DDE0D8', bgcolor: 'rgba(0,0,0,0.03)' },
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
